import type { HTMLAttributes } from 'react'
import { cn } from './cn'
import { Skeleton, SkeletonText } from './Skeleton'

export interface PageLoadingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
  /** Accessible loading label announced to AT. Default 'Loading'. */
  label?: string
  /** Number of placeholder body lines under the title bar. Default 3. */
  lines?: number
}

/**
 * PageLoading — full-area loading block for an entire page / panel while its data
 * loads. Composes the canonical Skeleton primitives (a title bar + a paragraph of
 * SkeletonText) so products stop hand-rolling full-page loading states.
 *
 * Announces busy state via role="status" / aria-busy with an sr-only label; the
 * decorative skeletons inside are hidden from AT.
 */
export function PageLoading({ label = 'Loading', lines = 3, className = '', ...props }: PageLoadingProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn('w-full px-5 py-12', className)}
      {...props}
    >
      <span className="sr-only">{label}</span>
      <div className="mx-auto w-full max-w-2xl flex flex-col gap-6" aria-hidden="true">
        <Skeleton width="w-1/3" height="h-6" />
        <SkeletonText lines={Math.max(1, lines)} />
      </div>
    </div>
  )
}
